import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASELINE } from '../../util';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';

const CourseComponent = () => {
  const navigate = useNavigate();

  // GET API로 courses 데이터 불러옴
  const [courses, setCourses] = useState([]);
  // 필터링 타입 (eng, math)
  const [filterType, setFilterType] = useState('eng');

  // Add Modal 상태 및 입력 필드
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCourseType, setNewCourseType] = useState('eng');
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [newCourseCreated, setNewCourseCreated] = useState(new Date().toISOString().split('T')[0]);
  const [newCourseContext, setNewCourseContext] = useState('');
  const [newCourseFrom, setNewCourseFrom] = useState('');
  const [newCourseStatus, setNewCourseStatus] = useState('open');

  // Edit Modal 상태 및 편집할 course 상태
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editCourseType, setEditCourseType] = useState('');
  const [editCourseTitle, setEditCourseTitle] = useState('');
  const [editCourseCreated, setEditCourseCreated] = useState('');
  const [editCourseContext, setEditCourseContext] = useState('');
  const [editCourseFrom, setEditCourseFrom] = useState('');
  const [editCourseStatus, setEditCourseStatus] = useState('');

  // 컴포넌트 마운트 시 courses 데이터 GET
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${BASELINE}api/courses`);
        const data = response.data;
        if (data.success) {
          setCourses(data.courses);
        } else {
          alert("Error fetching courses: " + data.message);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        alert("Failed to fetch courses. Please try again.");
      }
    };

    fetchCourses();
  }, []);

  // 드래그 앤 드롭 종료 핸들러
  const onDragEnd = async (result) => {
    if (!result.destination) return;

    // 필터링된 리스트에 한해 재정렬
    const filteredItems = courses.filter(course => course.type === filterType);
    const reorderedItems = Array.from(filteredItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    // 새로운 order_index 업데이트
    reorderedItems.forEach((item, index) => {
      item.order_index = index;
    });

    // 전체 courses 배열 업데이트 (필터 외 항목은 그대로 유지)
    const newCourses = courses.map(course => {
      const updated = reorderedItems.find(item => item.id === course.id);
      return updated || course;
    });

    setCourses(newCourses);

    // 새 순서 배열: 각 코스의 id 순서
    const newOrder = reorderedItems.map(course => course.id);

    try {
      await axios.put(`${BASELINE}api/courses/reorder`, { order: newOrder }, {
        headers: { 'Content-Type': 'application/json' }
      });
      // 옵션: 순서 업데이트 후 서버 데이터 재갱신
      const refreshResponse = await axios.get(`${BASELINE}api/courses`);
      if (refreshResponse.data.success) {
        setCourses(refreshResponse.data.courses);
      }
    } catch (error) {
      console.error("Error updating course order:", error);
      alert("Failed to update order. Please try again.");
    }
  };

  // 새 코스 추가 핸들러 (axios API 호출)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newCourse = {
      name: newCourseTitle,
      created: newCourseCreated,
      type: newCourseType,
      context: newCourseContext,
      from: newCourseFrom || 'chapter 1',
      status: newCourseStatus,
    };
    try {
      const response = await axios.post(`${BASELINE}api/courses`, newCourse, {
        headers: { 'Content-Type': 'application/json' }
      });
      const data = response.data;
      if (data.success) {
        setCourses([
          ...courses,
          {
            id: data.id,
            type: newCourseType,
            name: newCourseTitle,
            created: newCourseCreated,
            context: newCourseContext,
            from: newCourseFrom || 'chapter 1',
            status: newCourseStatus
          }
        ]);
        setNewCourseType('eng');
        setNewCourseTitle('');
        setNewCourseCreated(new Date().toISOString().split('T')[0]);
        setNewCourseContext('');
        setNewCourseFrom('');
        setNewCourseStatus('open');
        setIsModalOpen(false);
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error adding course:", error);
      alert("Failed to add course. Please try again.");
    }
  };

  // 삭제 핸들러 (2단계 확인 후 axios API 호출)
  const handleDelete = async (courseId) => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      if (window.confirm("One more time: Do you really want to delete this course?")) {
        try {
          const response = await axios.delete(`${BASELINE}api/courses/${courseId}`);
          const data = response.data;
          if (data.success) {
            setCourses(courses.filter(course => course.id !== courseId));
          } else {
            alert("Error: " + data.message);
          }
        } catch (error) {
          console.error("Error deleting course:", error);
          alert("Failed to delete course. Please try again.");
        }
      }
    }
  };

  // 수정 모달 열기
  const handleEditClick = (course) => {
    setEditingCourse(course);
    setEditCourseType(course.type);
    setEditCourseTitle(course.name);
    setEditCourseCreated(course.created);
    setEditCourseContext(course.context);
    setEditCourseFrom(course.from);
    setEditCourseStatus(course.status);
    setIsEditModalOpen(true);
  };

  // 수정 제출 핸들러 (axios API 호출)
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const updatedCourse = {
      name: editCourseTitle,
      created: editCourseCreated,
      type: editCourseType,
      context: editCourseContext,
      from: editCourseFrom || 'chapter 1',
      status: editCourseStatus,
    };
    try {
      const response = await axios.put(`${BASELINE}api/courses/${editingCourse.id}`, updatedCourse, {
        headers: { 'Content-Type': 'application/json' }
      });
      const data = response.data;
      if (data.success) {
        setCourses(
          courses.map(course => course.id === editingCourse.id
            ? { 
                ...course, 
                type: editCourseType, 
                name: editCourseTitle, 
                created: editCourseCreated, 
                context: editCourseContext, 
                from: editCourseFrom || 'chapter 1', 
                status: editCourseStatus 
              }
            : course
          )
        );
        setEditingCourse(null);
        setIsEditModalOpen(false);
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error updating course:", error);
      alert("Failed to update course. Please try again.");
    }
  };

  // 필터링된 코스 목록 (type에 따라), order_index 기준으로 정렬
  const filteredCourses = courses.filter(course => course.type === filterType)
    .sort((a, b) => {
      if (a.order_index !== undefined && b.order_index !== undefined) {
        return a.order_index - b.order_index;
      }
      return a.id - b.id;
    });

  // status에 따른 배경색 (open: 녹색, maintance: 주황색)
  const getBgColor = (status) => (status === 'open' ? 'bg-green-200' : 'bg-orange-200');

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Course</h1>

      {/* 타입 필터 버튼 */}
      <div className="mb-4">
        <button 
          onClick={() => setFilterType('eng')}
          className={`mr-2 px-4 py-2 rounded ${filterType === 'eng' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          English
        </button>
        <button 
          onClick={() => setFilterType('math')}
          className={`px-4 py-2 rounded ${filterType === 'math' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Math
        </button>
      </div>

      {/* DragDropContext 적용 - 필터링된 코스 목록을 드래그 앤 드롭으로 정렬 */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="courses">
          {(provided) => (
            <div className="grid grid-cols-3 gap-4" ref={provided.innerRef} {...provided.droppableProps}>
              {/* + 카드 (새 코스 추가용, 드래그 대상 아님) */}
              <div 
                className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:bg-gray-100"
                onClick={() => setIsModalOpen(true)}
              >
                <span className="text-4xl text-gray-500">+</span>
              </div>
              {/* 필터에 맞는 코스 카드들 */}
              {filteredCourses.map((course, index) => (
                <Draggable key={course.id} draggableId={course.id.toString()} index={index}>
                  {(provided) => (
                    <div
                      className={`border border-gray-300 rounded-lg p-4 ${getBgColor(course.status)}`}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      onClick={() => navigate(`/dev/class/${course.id}/lesson`)}
                    >
                      <h2 className="font-semibold">{course.name}</h2>
                      <p className="text-sm text-gray-500">Type: {course.type}</p>
                      <p className="text-sm text-gray-500">Created: {course.created}</p>
                      <p className="text-sm text-gray-500">Context: {course.context}</p>
                      <p className="text-sm text-gray-500">From: {course.from}</p>
                      <p className="text-sm text-gray-500">Status: {course.status}</p>
                      <div className="mt-2 flex space-x-2">
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleEditClick(course); }}
                          className="px-3 py-1 text-sm bg-green-500 text-white rounded"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDelete(course.id); }}
                          className="px-3 py-1 text-sm bg-red-500 text-white rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add New Course Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <div className="bg-white p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Add New Course</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Type</label>
                <select 
                  value={newCourseType}
                  onChange={(e) => setNewCourseType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="eng">English</option>
                  <option value="math">Math</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Title</label>
                <input 
                  type="text"
                  value={newCourseTitle}
                  onChange={(e) => setNewCourseTitle(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Created Date</label>
                <input 
                  type="date"
                  value={newCourseCreated}
                  onChange={(e) => setNewCourseCreated(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Context</label>
                <textarea 
                  value={newCourseContext}
                  onChange={(e) => setNewCourseContext(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">From</label>
                <input 
                  type="text"
                  value={newCourseFrom}
                  onChange={(e) => setNewCourseFrom(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="chapter 1"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Status</label>
                <select 
                  value={newCourseStatus}
                  onChange={(e) => setNewCourseStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="open">open</option>
                  <option value="maintance">maintance</option>
                </select>
              </div>
              <div className="flex justify-end">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="mr-2 px-4 py-2 border rounded"
                >
                  Close
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {isEditModalOpen && editingCourse && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <div className="bg-white p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Edit Course</h2>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Type</label>
                <select 
                  value={editCourseType}
                  onChange={(e) => setEditCourseType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="eng">English</option>
                  <option value="math">Math</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Title</label>
                <input 
                  type="text"
                  value={editCourseTitle}
                  onChange={(e) => setEditCourseTitle(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Created Date</label>
                <input 
                  type="date"
                  value={editCourseCreated}
                  onChange={(e) => setEditCourseCreated(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Context</label>
                <textarea 
                  value={editCourseContext}
                  onChange={(e) => setEditCourseContext(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">From</label>
                <input 
                  type="text"
                  value={editCourseFrom}
                  onChange={(e) => setEditCourseFrom(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="chapter 1"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Status</label>
                <select 
                  value={editCourseStatus}
                  onChange={(e) => setEditCourseStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="open">open</option>
                  <option value="maintance">maintance</option>
                </select>
              </div>
              <div className="flex justify-end">
                <button 
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="mr-2 px-4 py-2 border rounded"
                >
                  Close
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default CourseComponent;
